import { useCallback, useEffect, useRef, useState } from 'react';

import type { TranscriptElement } from '@src/types.ts/transcript';
import type { HighlightRange } from '@src/utility/transcriptQuoteUtils';
import type React from 'react';

export type AdjustHandle = 'start' | 'end';

export type AdjustRange = { start: number; end: number };

interface UseOverlayRangeAdjustOpts {
  /** Overlay currently in adjust mode, or null. */
  adjustingOverlayId: string | null;
  /** The committed source-element index range for the adjusting overlay. */
  committedRange: HighlightRange | null;
  /** Source-language transcript elements (index space == `data-index`). */
  transcriptElements: ReadonlyArray<TranscriptElement>;
  /** Resolve the local scroll container — all DOM hit-tests are scoped to it. */
  getScrollContainer: () => HTMLElement | null | undefined;
  /** DOM attribute carrying the word index (always `data-index` here). */
  spanIndexAttr: string;
  /** Commit a new whole-word range for the overlay. */
  onCommit: (overlayId: string, range: AdjustRange) => void;
}

interface UseOverlayRangeAdjustResult {
  /** Tentative range while a handle is being dragged; null when idle. */
  previewRange: AdjustRange | null;
  /** Which handle is actively dragging, or null. */
  activeHandle: AdjustHandle | null;
  /** Live cursor position during a drag (for smooth handle follow); else null. */
  pointer: { x: number; y: number } | null;
  /** Start a drag from a handle's pointer-down. */
  beginDrag: (handle: AdjustHandle, e: React.PointerEvent) => void;
  /** Keyboard nudge: move a boundary by one surviving word and commit. */
  nudgeBoundary: (handle: AdjustHandle, direction: -1 | 1) => void;
}

/** Runtime `ts` can be null for removed/cut words despite the type. */
function tsOf(el: TranscriptElement | undefined): number | null {
  const v = (el as { ts?: unknown } | undefined)?.ts;
  return typeof v === 'number' ? v : null;
}

/**
 * Walk from `idx` toward `direction` to the nearest word with a non-null
 * timestamp (mirrors `findOverlayRanges`, which never opens/extends a range
 * on a deleted word). Returns the original index if `idx` itself survives, or
 * if no surviving word is found in that direction.
 *
 * Exported for unit tests.
 */
export function snapToSurviving(
  idx: number,
  direction: -1 | 1,
  elements: ReadonlyArray<TranscriptElement>,
): number {
  if (idx < 0 || idx >= elements.length) return idx;
  if (tsOf(elements[idx]) != null) return idx;
  for (let i = idx + direction; i >= 0 && i < elements.length; i += direction) {
    if (tsOf(elements[i]) != null) return i;
  }
  return idx;
}

/**
 * Pure boundary math: given the handle being dragged, the index hit under the
 * pointer, the current base range, and the elements, return the new range —
 * snapped to a surviving word and clamped so it never inverts (single-word
 * floor allowed). Exported for unit tests.
 */
export function computeAdjustedRange(
  handle: AdjustHandle,
  hitIdx: number,
  base: AdjustRange,
  elements: ReadonlyArray<TranscriptElement>,
): AdjustRange {
  if (handle === 'start') {
    const dir: -1 | 1 = hitIdx < base.start ? -1 : 1;
    const start = Math.min(snapToSurviving(hitIdx, dir, elements), base.end);
    return { start, end: base.end };
  }
  const dir: -1 | 1 = hitIdx > base.end ? 1 : -1;
  const end = Math.max(snapToSurviving(hitIdx, dir, elements), base.start);
  return { start: base.start, end };
}

/**
 * Hit-test the word index under the pointer, scoped to `container`.
 * `document.elementFromPoint` is document-global, so we reject spans that
 * live outside our container (guards against the duplicate hidden transcript
 * instance — see CLAUDE.md item 12). Falls back to the nearest span by rect
 * when the pointer is in a line-gap.
 */
function indexUnderPointer(
  clientX: number,
  clientY: number,
  container: HTMLElement,
  attr: string,
): number | null {
  const point = document.elementFromPoint(clientX, clientY);
  let span =
    point instanceof HTMLElement ? point.closest<HTMLElement>(`[${attr}]`) : null;
  // Reject spans outside our container — `elementFromPoint` is document-global
  // and the clip-preview modal mounts a second transcript (CLAUDE.md #12).
  if (span && !container.contains(span)) span = null;

  if (!span) {
    span = nearestSpanByRect(container, clientX, clientY, attr);
  }
  const idx = span ? Number(span.getAttribute(attr)) : null;
  if (idx == null) return null;
  return Number.isNaN(idx) ? null : idx;
}

/**
 * Find the indexed span closest to (x, y): prefer spans whose vertical band
 * contains y, then minimise horizontal distance; otherwise minimise overall
 * distance. Bounded scan — transcript spans number in the thousands, but a
 * drag fires this at pointer-move cadence, so keep it linear and cheap.
 */
function nearestSpanByRect(
  container: HTMLElement,
  x: number,
  y: number,
  attr: string,
): HTMLElement | null {
  const spans = container.querySelectorAll<HTMLElement>(`[${attr}]`);
  let best: HTMLElement | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  spans.forEach(span => {
    const r = span.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return;
    const onLine = y >= r.top && y <= r.bottom;
    const dx = x < r.left ? r.left - x : x > r.right ? x - r.right : 0;
    const dy = y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0;
    // Strongly prefer same-line matches so a gap hover snaps to the line above.
    const score = onLine ? dx : dx + dy * 1000;
    if (score < bestScore) {
      bestScore = score;
      best = span;
    }
  });
  return best;
}

/**
 * Drag/keyboard logic for re-anchoring an overlay's transcript word range.
 * Owns the preview state and the document-level pointer listeners; commits a
 * whole-word range on pointer-up. UI/positioning lives in OverlayRangeHandles.
 */
export function useOverlayRangeAdjust(
  opts: UseOverlayRangeAdjustOpts,
): UseOverlayRangeAdjustResult {
  const {
    adjustingOverlayId,
    committedRange,
    transcriptElements,
    getScrollContainer,
    spanIndexAttr,
    onCommit,
  } = opts;

  const [previewRange, setPreviewRange] = useState<AdjustRange | null>(null);
  const [activeHandle, setActiveHandle] = useState<AdjustHandle | null>(null);
  // Live pointer position during a drag — lets the active handle follow the
  // cursor smoothly (pixel-precise) even though the underline range snaps to
  // whole words. Null when not dragging.
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  // Mirror live values into refs so the document listeners (bound once per
  // drag) always read current state without re-binding.
  const previewRef = useRef<AdjustRange | null>(null);
  previewRef.current = previewRange;
  const committedRef = useRef<HighlightRange | null>(committedRange);
  committedRef.current = committedRange;
  const elementsRef = useRef(transcriptElements);
  elementsRef.current = transcriptElements;
  const overlayIdRef = useRef(adjustingOverlayId);
  overlayIdRef.current = adjustingOverlayId;

  // Reset preview whenever the adjusting overlay changes / clears.
  useEffect(() => {
    setPreviewRange(null);
    setActiveHandle(null);
    setPointer(null);
  }, [adjustingOverlayId]);

  const computeNext = useCallback(
    (handle: AdjustHandle, hitIdx: number): AdjustRange | null => {
      const base = previewRef.current ?? committedRef.current;
      if (!base) return null;
      return computeAdjustedRange(
        handle,
        hitIdx,
        { start: base.start, end: base.end },
        elementsRef.current,
      );
    },
    [],
  );

  const beginDrag = useCallback(
    (handle: AdjustHandle, e: React.PointerEvent) => {
      const container = getScrollContainer();
      if (!container || !committedRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      setActiveHandle(handle);
      setPreviewRange({
        start: committedRef.current.start,
        end: committedRef.current.end,
      });

      const onMove = (ev: PointerEvent) => {
        // Smooth follow: track the raw cursor position for the active handle.
        setPointer({ x: ev.clientX, y: ev.clientY });
        const hit = indexUnderPointer(
          ev.clientX,
          ev.clientY,
          container,
          spanIndexAttr,
        );
        if (hit == null) return;
        const next = computeNext(handle, hit);
        if (next) {
          setPreviewRange(next);
        }
      };

      const onUp = () => {
        document.removeEventListener('pointermove', onMove, true);
        document.removeEventListener('pointerup', onUp, true);
        document.removeEventListener('pointercancel', onUp, true);
        const id = overlayIdRef.current;
        const next = previewRef.current;
        const committed = committedRef.current;
        setActiveHandle(null);
        setPreviewRange(null);
        setPointer(null);
        const willCommit = !!(
          id &&
          next &&
          committed &&
          (next.start !== committed.start || next.end !== committed.end)
        );
        if (willCommit && id && next) {
          onCommit(id, next);
        }
      };

      document.addEventListener('pointermove', onMove, true);
      document.addEventListener('pointerup', onUp, true);
      document.addEventListener('pointercancel', onUp, true);
    },
    [getScrollContainer, spanIndexAttr, computeNext, onCommit],
  );

  const nudgeBoundary = useCallback(
    (handle: AdjustHandle, direction: -1 | 1) => {
      const id = overlayIdRef.current;
      const base = committedRef.current;
      if (!id || !base) return;
      const els = elementsRef.current;
      if (handle === 'start') {
        const target = snapToSurviving(base.start + direction, direction, els);
        const start = Math.min(Math.max(target, 0), base.end);
        if (start !== base.start) onCommit(id, { start, end: base.end });
      } else {
        const target = snapToSurviving(base.end + direction, direction, els);
        const end = Math.max(
          Math.min(target, els.length - 1),
          base.start,
        );
        if (end !== base.end) onCommit(id, { start: base.start, end });
      }
    },
    [onCommit],
  );

  return { previewRange, activeHandle, pointer, beginDrag, nudgeBoundary };
}
